import { RefCallback, useCallback, useRef, useState } from "react";

// Uses a callback ref instead of useRef so the ResizeObserver re-attaches
// whenever the DOM element is remounted (e.g. inside AnimatePresence/Collapse).
// A useRef-based approach only runs its effect once because the ref object
// identity never changes, leaving the observer attached to a stale node.
function useElementResizeObserver<T extends HTMLElement>(): [
  RefCallback<T>,
  number,
  number
] {
  const observerRef = useRef<ResizeObserver | null>(null);

  const [width, setWidth] = useState<number>(0);
  const [height, setHeight] = useState<number>(0);

  const ref = useCallback((element: T | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (element) {
      const observer = new ResizeObserver(() => {
        setWidth(element.clientWidth);
        setHeight(element.clientHeight);
      });
      observer.observe(element);
      observerRef.current = observer;
    }
  }, []);

  return [ref, width, height];
}

export default useElementResizeObserver;
